import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSetTopbarCrumbs } from "@marekl/adminer/shell";
import { Page, Tabs, Card, Alert, Button, type TabItem } from "@marekl/adminer/components";
import { VoucherTypesCard } from "./VoucherTypesCard";
import { MerchKindsCard } from "./MerchKindsCard";
import { EshopProductMapCard } from "./EshopProductMapCard";
import styles from "./ConfigPage.module.css";

type TabId = "products" | "eshop" | "claim-barcode";

const TABS: TabItem[] = [
  { id: "products", label: "Produkty" },
  { id: "eshop", label: "E-shop" },
  { id: "claim-barcode", label: "Claim barcode" },
];

function EshopTab() {
  const navigate = useNavigate();
  return (
    <div className={styles.eshopStack}>
      <Alert variant="info" title="Konfigurace připojení k e-shopu">
        <p>
          URL objednávkového API (včetně hesla) se nastavuje v{" "}
          <code>config.yaml</code> v klíči <code>eshop.base_url</code> nebo
          prostřednictvím proměnné prostředí{" "}
          <code>KARTIS_ESHOP_BASE_URL</code>. Heslo je v logu automaticky
          skryto.
        </p>
        <p>
          Platební metody pro import se konfigurují jako seznam názvů (pole{" "}
          <code>payment_method_names</code>), odpovídajících poli{" "}
          <code>nazev_platba</code> v API e-shopu — např.{" "}
          <em>Platební karta</em>, <em>Online bankovní převod</em>. Všechny
          odpovídající objednávky jsou importovány ihned (nejprve jako{" "}
          <em>čekající</em>, po potvrzení platby jako <em>zaplacené</em>).
        </p>
        <p>
          Výchozí interval synchronizace: 10 minut; rozsah znovu-prohledávání:
          48 hodin. Tyto hodnoty nelze měnit v aplikaci — kontaktujte správce
          systému.
        </p>
      </Alert>

      <EshopProductMapCard />

      <Card
        header={<Card.Header title="Synchronizace e-shopu" />}
      >
        <p className={styles.cardDesc}>
          Ruční spuštění importu objednávek z e-shopu, přehled stavu
          synchronizace a posledních importovaných objednávek.
        </p>
        <Button variant="primary" onClick={() => navigate("/eshop-sync")}>
          Otevřít synchronizaci
        </Button>
      </Card>
    </div>
  );
}

function ClaimBarcodeTab() {
  return (
    <div className={styles.infoStack}>
      <Card
        header={<Card.Header title="Claim barcode — vyhrazený rozsah" />}
      >
        <p className={styles.cardDesc}>
          Certifikáty vydané systémem jsou označeny EAN-13 čárovým kódem
          z vyhrazeného rozsahu. Výchozí rozsah má prefix{" "}
          <code>02</code> a subprefix <code>9</code> (tj. kódy začínající{" "}
          <code>029…</code>).
        </p>
        <p className={styles.cardDesc}>
          Importér čárových kódů (barcode bank) automaticky odmítá kódy v
          tomto rozsahu, aby nedošlo ke konfliktu s voucherovými čárovými
          kódy.
        </p>
        <Alert variant="info" title="Konfigurace prefixu">
          Prefix a subprefix rozsahu claim barcode jsou součástí konfigurace
          nasazení a nelze je měnit v aplikaci. Změna vyžaduje aktualizaci{" "}
          <code>config.yaml</code> a nové nasazení aplikace — kontaktujte
          správce systému.
        </Alert>
      </Card>
    </div>
  );
}

export function ConfigPage() {
  const [activeTab, setActiveTab] = useState<TabId>("products");

  useSetTopbarCrumbs([{ label: "Konfigurace" }]);

  return (
    <>
      <Page.Content>
        <Page.Header
          title="Konfigurace"
          subtitle="e-shop, typy voucherů, mapování produktů"
          subtitleVariant="tagline"
        />

        <Tabs
          tabs={TABS}
          value={activeTab}
          onChange={(id) => setActiveTab(id as TabId)}
          aria-label="Sekce konfigurace"
        />

        {activeTab === "products" && (
          <div className={styles.eshopStack}>
            <VoucherTypesCard />
            <MerchKindsCard />
          </div>
        )}
        {activeTab === "eshop" && <EshopTab />}
        {activeTab === "claim-barcode" && <ClaimBarcodeTab />}
      </Page.Content>
    </>
  );
}

